import { useEffect, useRef, useState } from "react";
import { Marker } from 'maplibre-gl';
import { MapType, markerDetailsType } from "../../../../types/dataTypes";
import { getGeoJsonByName } from "../../../../services/getGeojsonData.service";
import { LAYER_ORDER_ARRAY, mapMarker } from "../../../../utils/mapConsts";
import { COLORS, SELECTED_LAYER_INIITIAL } from '../../../../utils/consts';
import DetailedPopup from "../../DetailedPopup";

type MarkerLayerProps = {
  map: MapType;
  mapLoaded: boolean;
  geojsonName: string;
  layerName: string;
  layerId: string;
  icon?: any;
  detailDefinition: markerDetailsType[] | undefined;
};

function MarkerLayer({ map, mapLoaded, geojsonName, layerId, icon, detailDefinition, layerName }: MarkerLayerProps) {
  const mapCurrent = map.current;
  const markerClass = SELECTED_LAYER_INIITIAL.find((layer: any) => layer.layerId === layerId)?.markerClass;
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const [detailsOpen, setDetailsOpen] = useState<Boolean>(false);
  const [selectedDetails, setSelectedDetails] = useState<markerDetailsType[] | undefined>(undefined);
  const [previousMarker, setPreviousMarker] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!mapLoaded || !mapCurrent) return;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const markerSize = isMobile ? 34 : 25;

    getGeoJsonByName(geojsonName).then((res) => {
      if (res.statusCode === 500) {
        console.error("500 Error: data not available");
        return;
      }

      res.features.forEach((feature: any) => {
        const coordsTyped: [number, number] = feature.geometry.coordinates;
        const el = document.createElement('div');

        if (typeof icon === 'string') {
          const img = document.createElement('img');
          img.src = icon;
          img.style.width = `${markerSize}px`;
          img.style.height = `${markerSize}px`;
          el.appendChild(img);
        } else {
          el.innerHTML = mapMarker(icon, '#fff', COLORS.MAP.ICONS, 600, 500, markerSize);
        }

        el.style.minWidth = `${markerSize}px`;
        el.style.minHeight = `${markerSize}px`;
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.touchAction = 'manipulation';

        if (markerClass) {
          el.className = markerClass;
        }

        const marker = new Marker({ element: el }).setLngLat(coordsTyped).addTo(mapCurrent);
        markersRef.current.push(marker);

        if (detailDefinition) {
          const openDetails = (e: Event) => {
            e.stopPropagation();

            // Remove the black circle from the previous marker
            if (previousMarker) {
              previousMarker.style.border = '';
              previousMarker.style.borderRadius = '';
            }

            const featureProperties = feature.properties;
            const updatedDetails = detailDefinition.map(detail => ({
              ...detail,
              value: featureProperties[detail.key]?.toString() || ''
            }));

            setSelectedDetails(updatedDetails);
            setDetailsOpen(true);

            // Draw a black circle around the new marker
            el.style.border = '2px solid black';
            el.style.borderRadius = '50%';

            // Update the previous marker
            setPreviousMarker(el);
          };

          el.addEventListener('click', openDetails);
          el.addEventListener('touchend', openDetails);

          el.addEventListener('mouseover', () => {
            el.style.cursor = 'pointer';
          });

          el.addEventListener('mouseout', () => {
            el.style.cursor = '';
          });
        }
      });

    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, [mapLoaded, mapCurrent, geojsonName, layerId, icon, detailDefinition, markerClass, previousMarker]);

  return (
    <>
      {detailsOpen && selectedDetails && (
        <DetailedPopup
          details={selectedDetails}
          onClose={() => setDetailsOpen(false)}
          map={map}
          layerName={layerName}
        />
      )}
    </>
  );
}

export default MarkerLayer;